import { getCSRFToken } from "./csrf.ts";
import {
  normalizeAiInsight,
  normalizeAiInsightList,
  normalizeBlog,
  normalizeBlogComment,
  normalizeBlogComments,
  normalizeBlogList,
  normalizeBlogMediaList,
  normalizeCompletedAiInsightList,
  normalizeAiInsightsRequestItems,
  normalizeBatchMacrosResponse,
  normalizeFoodSearchResponse,
  normalizePantryItemsResponse,
  normalizeScan,
  normalizeScanList,
  normalizeVisitList,
} from "./normalize.ts";
import type {
  AIInsightRequest,
  AIInsightCompletedListResponse,
  AIInsightRequestListResponse,
  AdminUserDailyCostResponse,
  AdminUserLifetimeCost,
  AdminUserLifetimeCostsPageResponse,
  BlogComment,
  BlogMedia,
  BlogCommentsResponse,
  BlogListResponse,
  BlogPost,
  BlogStructuredContent,
  FdcBatchMacrosResponse,
  FdcFoodSearchResponse,
  GenericApiError,
  HttpMethod,
  PantryItemsResponse,
  PantryScanSubmission,
  SessionState,
  VisitRecord,
} from "../types";

export class ApiRequestError extends Error {
  status: number;
  payload: GenericApiError | null;

  constructor(message: string, status: number, payload: GenericApiError | null) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

export interface BlogCommentsQuery {
  limit?: number;
  offset?: number;
  topLevelOnly?: boolean;
  parentCommentId?: string | null;
}

export interface BlogMutationPayload {
  title: string;
  summary?: string | null;
  body: string;
  tags: string[];
  status: "DRAFT" | "PUBLISHED";
  hero_media?: BlogMedia | null;
  media?: BlogMedia[];
  structuredContent?: BlogStructuredContent | null;
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function request<T>(url: string, method: HttpMethod, body?: BodyInit | null, headers?: HeadersInit): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(headers ?? {}),
      ...(method === "GET" || method === "DELETE" ? {} : { "X-CSRFToken": getCSRFToken() }),
    },
    body,
  });

  const payload = (await parseJson(response)) as GenericApiError | T | null;

  if (!response.ok) {
    const detail = (payload as GenericApiError | null)?.detail ?? `Request failed with status ${response.status}`;
    throw new ApiRequestError(detail, response.status, (payload as GenericApiError | null) ?? null);
  }

  return (payload as T) ?? ({} as T);
}

function toQuery(params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `?${query}` : "";
}

export function fetchSession(): Promise<SessionState> {
  return request<SessionState>("/app/api/session/", "GET");
}

export function createEarlyAccessSignup(payload: {
  email: string;
  roleInterest: "user" | "dietitian" | "both";
}): Promise<{ ok: boolean; created: boolean; email: string; roleInterest: string }> {
  return request<{ ok: boolean; created: boolean; email: string; roleInterest: string }>(
    "/app/api/early-access/",
    "POST",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  );
}

export function submitOnboardingProfile(payload: unknown): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    "/app/api/onboarding/profile/",
    "POST",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  );
}

export function logoutSession(): Promise<{ ok: boolean; redirectUrl: string }> {
  return request<{ ok: boolean; redirectUrl: string }>("/app/api/session/logout/", "POST", null);
}

export function fetchScans(): Promise<PantryScanSubmission[]> {
  return request<unknown>("/app/api/v1/pantry-scans", "GET").then((payload) => normalizeScanList(payload));
}

export function createScan(formData: FormData): Promise<PantryScanSubmission> {
  return request<unknown>("/app/api/v1/pantry-scans", "POST", formData).then((payload) => {
    const scan = normalizeScan(payload);
    if (!scan) {
      throw new Error("Invalid scan response.");
    }
    return scan;
  });
}

export function fetchScanDetail(scanId: string): Promise<PantryScanSubmission> {
  return request<unknown>(`/app/api/v1/pantry-scans/${scanId}`, "GET").then((payload) => {
    const scan = normalizeScan(payload);
    if (!scan) {
      throw new Error("Invalid scan response.");
    }
    return scan;
  });
}

export function fetchScanResults(scanId: string): Promise<unknown> {
  return request<unknown>(`/app/api/v1/pantry-scans/${scanId}/results`, "GET");
}

export function reprocessScan(scanId: string): Promise<PantryScanSubmission> {
  return request<unknown>(`/app/api/v1/pantry-scans/${scanId}/reprocess`, "POST", null).then((payload) => {
    const scan = normalizeScan(payload);
    if (!scan) {
      throw new Error("Invalid scan response.");
    }
    return scan;
  });
}

export function deleteScan(scanId: string): Promise<void> {
  return request<void>(`/app/api/v1/pantry-scans/${scanId}`, "DELETE");
}

export function fetchPantryItems(limit = 50, cursor?: string | null): Promise<PantryItemsResponse> {
  const query = toQuery({ limit, cursor: cursor ?? undefined });
  return request<unknown>(`/app/api/v1/pantry/items${query}`, "GET").then((payload) => normalizePantryItemsResponse(payload));
}

export function updatePantryItem(itemId: string, payload: Record<string, unknown>): Promise<unknown> {
  return request<unknown>(
    `/app/api/v1/pantry/items/${itemId}`,
    "PATCH",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  );
}

export function deletePantryItem(itemId: string): Promise<void> {
  return request<void>(`/app/api/v1/pantry/items/${itemId}`, "DELETE");
}

export function fetchAiInsights(): Promise<AIInsightRequest[] | AIInsightRequestListResponse> {
  return request<unknown>("/app/api/v1/ai-insights/jobs/pantry", "GET").then((payload) => normalizeAiInsightList(payload));
}

export function createAiInsights(payload: Record<string, unknown>): Promise<AIInsightRequest> {
  const pantryItemsSource = Array.isArray(payload.pantry_items)
    ? payload.pantry_items
    : Array.isArray(payload.pantryItems)
      ? payload.pantryItems
      : [];

  const requestPayload = {
    pantry_items: normalizeAiInsightsRequestItems(pantryItemsSource),
  };

  return request<unknown>(
    "/app/api/v1/ai-insights/jobs/pantry",
    "POST",
    JSON.stringify(requestPayload),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const insight = normalizeAiInsight(responsePayload);
    if (!insight) {
      throw new Error("Invalid AI insight response.");
    }
    return insight;
  });
}

export function fetchAiInsightStatus(jobId: string): Promise<AIInsightRequest> {
  return request<unknown>(`/app/api/v1/ai-insights/jobs/${jobId}`, "GET").then((payload) => {
    const insight = normalizeAiInsight(payload);
    if (!insight) {
      throw new Error("Invalid AI insight status response.");
    }
    return insight;
  });
}

export function fetchAiInsightResult(jobId: string): Promise<unknown> {
  return request<unknown>(`/app/api/v1/ai-insights/jobs/${jobId}/result`, "GET");
}

export function updateAiInsightResult(jobId: string, payload: Record<string, unknown>): Promise<unknown> {
  const normalizedPayload =
    payload && typeof payload === "object" && payload.result && typeof payload.result === "object"
      ? payload
      : { result: payload };
  return request<unknown>(
    `/app/api/v1/ai-insights/jobs/${jobId}/result`,
    "PUT",
    JSON.stringify(normalizedPayload),
    { "Content-Type": "application/json" },
  );
}

export function fetchCompletedAiInsights(days = 7, cursor?: string | null, limit = 25): Promise<AIInsightCompletedListResponse> {
  const safeLimit = Math.min(100, Math.max(1, Math.trunc(limit)));
  const query = toQuery({ days, cursor: cursor ?? undefined, limit: safeLimit });
  return request<unknown>(`/app/api/v1/ai-insights/jobs/complete${query}`, "GET").then((payload) =>
    normalizeCompletedAiInsightList(payload),
  );
}

export function fetchBlogs(limit = 12, offset = 0): Promise<BlogListResponse> {
  const query = toQuery({ limit, offset });
  return request<unknown>(`/app/api/v1/blogs${query}`, "GET").then((payload) => normalizeBlogList(payload));
}

export function fetchMyBlogs(limit = 20, offset = 0): Promise<BlogListResponse> {
  const query = toQuery({ limit, offset });
  return request<unknown>(`/app/api/v1/blogs/mine${query}`, "GET").then((payload) => normalizeBlogList(payload));
}

export function fetchBlogDetail(blogId: string): Promise<BlogPost> {
  return request<unknown>(`/app/api/v1/blogs/${blogId}`, "GET").then((payload) => {
    const blog = normalizeBlog(payload);
    if (!blog) {
      throw new Error("Invalid blog response.");
    }
    return blog;
  });
}

export function createBlog(payload: BlogMutationPayload): Promise<BlogPost> {
  return request<unknown>(
    "/app/api/v1/blogs",
    "POST",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const blog = normalizeBlog(responsePayload);
    if (!blog) {
      throw new Error("Invalid blog response.");
    }
    return blog;
  });
}

export function updateBlog(
  blogId: string,
  payload: BlogMutationPayload,
): Promise<BlogPost> {
  return request<unknown>(
    `/app/api/v1/blogs/${blogId}`,
    "PATCH",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const blog = normalizeBlog(responsePayload);
    if (!blog) {
      throw new Error("Invalid blog response.");
    }
    return blog;
  });
}

export function fetchBlogComments(
  blogId: string,
  limitOrOptions: number | BlogCommentsQuery = 25,
  offset = 0,
): Promise<BlogCommentsResponse> {
  const options: BlogCommentsQuery =
    typeof limitOrOptions === "number" ? { limit: limitOrOptions, offset } : limitOrOptions;
  const query = toQuery({
    limit: options.limit ?? 25,
    offset: options.offset ?? 0,
    top_level_only: options.topLevelOnly ? "true" : undefined,
    parent_comment_id: options.parentCommentId ?? undefined,
  });
  return request<unknown>(`/app/api/v1/blogs/${blogId}/comments${query}`, "GET").then((payload) => normalizeBlogComments(payload));
}

export function createBlogComment(blogId: string, body: string, parentCommentId?: string | null): Promise<BlogComment> {
  const requestPayload: Record<string, unknown> = { body };
  if (parentCommentId && parentCommentId.trim()) {
    requestPayload.parent_comment_id = parentCommentId.trim();
  }
  return request<unknown>(
    `/app/api/v1/blogs/${blogId}/comments`,
    "POST",
    JSON.stringify(requestPayload),
    { "Content-Type": "application/json" },
  ).then((payload) => {
    const comment = normalizeBlogComment(payload);
    if (!comment) {
      throw new Error("Invalid blog comment response.");
    }
    return comment;
  });
}

export function voteBlog(blogId: string, value: 1 | -1): Promise<void> {
  return request<void>(
    `/app/api/v1/blogs/${blogId}/votes`,
    "POST",
    JSON.stringify({ value }),
    { "Content-Type": "application/json" },
  );
}

export function clearBlogVote(blogId: string): Promise<void> {
  return request<void>(`/app/api/v1/blogs/${blogId}/votes`, "DELETE");
}

export function uploadBlogMedia(formData: FormData): Promise<BlogMedia[]> {
  return request<unknown>("/app/api/dietitian/blog-media-upload/", "POST", formData).then((payload) =>
    normalizeBlogMediaList(payload),
  );
}

export function fetchVisits(params?: {
  status?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
  offset?: number;
}): Promise<VisitRecord[]> {
  const limit = Math.min(100, Math.max(1, Math.trunc(params?.limit ?? 50)));
  const offset = Math.max(0, Math.trunc(params?.offset ?? 0));
  const query = toQuery({
    status: params?.status ?? undefined,
    from: params?.from ?? undefined,
    to: params?.to ?? undefined,
    limit,
    offset,
  });
  return request<unknown>(`/app/api/v1/visits${query}`, "GET")
    .then((payload) => normalizeVisitList(payload))
    .catch((error) => {
      if (error instanceof ApiRequestError && error.status === 422 && (params?.from || params?.to)) {
        const fallbackQuery = toQuery({
          status: params?.status ?? undefined,
          limit,
          offset,
        });
        return request<unknown>(`/app/api/v1/visits${fallbackQuery}`, "GET").then((payload) => normalizeVisitList(payload));
      }
      throw error;
    });
}

export function createVisit(payload: {
  dietitian_id?: string | null;
  type: "VIDEO" | "IN_PERSON";
  requested_start_at?: string | null;
  requested_end_at?: string | null;
  preferred_slots?: Array<{ start_at: string; end_at: string }> | null;
  timezone?: string | null;
  location?: string | null;
  notes?: string | null;
  family_member_id?: string | null;
  idempotency_key?: string | null;
}): Promise<VisitRecord> {
  return request<unknown>(
    "/app/api/v1/visits",
    "POST",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const records = normalizeVisitList(responsePayload);
    const record = records[0] ?? null;
    if (record) {
      return record;
    }
    const fallback = normalizeVisitList({ items: [responsePayload] })[0];
    if (!fallback) {
      throw new Error("Invalid visit response.");
    }
    return fallback;
  });
}

export function updateVisit(
  visitId: string,
  payload: {
    status?: "REQUESTED" | "CONFIRMED" | "RESCHEDULED" | "CANCELED" | "COMPLETED";
    requested_start_at?: string | null;
    requested_end_at?: string | null;
    preferred_slots?: Array<{ start_at: string; end_at: string }> | null;
    confirmed_start_at?: string | null;
    confirmed_end_at?: string | null;
    timezone?: string | null;
    location?: string | null;
    notes?: string | null;
    meeting_url?: string | null;
    meeting_metadata?: string | null;
    cancellation_reason?: string | null;
  },
): Promise<VisitRecord> {
  return request<unknown>(
    `/app/api/v1/visits/${visitId}`,
    "PATCH",
    JSON.stringify(payload),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const records = normalizeVisitList(responsePayload);
    const record = records[0] ?? null;
    if (record) {
      return record;
    }
    const fallback = normalizeVisitList({ items: [responsePayload] })[0];
    if (!fallback) {
      throw new Error("Invalid visit response.");
    }
    return fallback;
  });
}

export function fetchDietitianPendingVisits(params?: {
  limit?: number;
  offset?: number;
  from?: string | null;
  to?: string | null;
}): Promise<VisitRecord[]> {
  const limit = Math.min(100, Math.max(1, Math.trunc(params?.limit ?? 50)));
  const offset = Math.max(0, Math.trunc(params?.offset ?? 0));
  const query = toQuery({
    limit,
    offset,
    from: params?.from ?? undefined,
    to: params?.to ?? undefined,
  });
  return request<unknown>(`/app/api/v1/dietitian/visits/pending${query}`, "GET")
    .then((payload) => normalizeVisitList(payload))
    .catch((error) => {
      if (error instanceof ApiRequestError && error.status === 422 && (params?.from || params?.to)) {
        const fallbackQuery = toQuery({ limit, offset });
        return request<unknown>(`/app/api/v1/dietitian/visits/pending${fallbackQuery}`, "GET").then((payload) =>
          normalizeVisitList(payload),
        );
      }
      throw error;
    });
}

export function fetchDietitianVisitHistory(params?: {
  status?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
  offset?: number;
}): Promise<VisitRecord[]> {
  const limit = Math.min(100, Math.max(1, Math.trunc(params?.limit ?? 50)));
  const offset = Math.max(0, Math.trunc(params?.offset ?? 0));
  const query = toQuery({
    status: params?.status ?? undefined,
    from: params?.from ?? undefined,
    to: params?.to ?? undefined,
    limit,
    offset,
  });
  return request<unknown>(`/app/api/v1/dietitian/visits/history${query}`, "GET")
    .then((payload) => normalizeVisitList(payload))
    .catch((error) => {
      if (error instanceof ApiRequestError && error.status === 422 && (params?.from || params?.to)) {
        const fallbackQuery = toQuery({
          status: params?.status ?? undefined,
          limit,
          offset,
        });
        return request<unknown>(`/app/api/v1/dietitian/visits/history${fallbackQuery}`, "GET").then((payload) =>
          normalizeVisitList(payload),
        );
      }
      throw error;
    });
}

export function acceptDietitianVisit(visitId: string, slotIndex: number): Promise<VisitRecord> {
  return request<unknown>(
    `/app/api/v1/dietitian/visits/${visitId}/accept`,
    "POST",
    JSON.stringify({ slot_index: slotIndex }),
    { "Content-Type": "application/json" },
  ).then((responsePayload) => {
    const records = normalizeVisitList(responsePayload);
    const record = records[0] ?? null;
    if (record) {
      return record;
    }
    const fallback = normalizeVisitList({ items: [responsePayload] })[0];
    if (!fallback) {
      throw new Error("Invalid visit response.");
    }
    return fallback;
  });
}

export function searchFoods(query: string, source = "all", limit = 25, offset = 0): Promise<FdcFoodSearchResponse> {
  const qs = toQuery({ q: query, source, limit, offset });
  return request<unknown>(`/app/api/v1/fdc/foods/search${qs}`, "GET").then((payload) => normalizeFoodSearchResponse(payload));
}

export function fetchFoodMacrosBatch(
  items: Array<{ source: string; fdcId?: number; fdc_id?: number }>,
): Promise<FdcBatchMacrosResponse> {
  const normalizedItems = items
    .map((item) => ({
      source: item.source,
      fdc_id: item.fdc_id ?? item.fdcId ?? null,
    }))
    .filter((item) => typeof item.source === "string" && Number.isFinite(item.fdc_id) && Number(item.fdc_id) > 0);

  return request<unknown>(
    "/app/api/v1/fdc/macros/batch",
    "POST",
    JSON.stringify({ items: normalizedItems }),
    { "Content-Type": "application/json" },
  ).then((payload) => normalizeBatchMacrosResponse(payload));
}

export function fetchFoodDetail(source: string, fdcId: number): Promise<unknown> {
  return request<unknown>(`/app/api/v1/fdc/foods/${source}/${fdcId}`, "GET");
}

export function fetchFoodNutrients(source: string, fdcId: number): Promise<unknown> {
  return request<unknown>(`/app/api/v1/fdc/foods/${source}/${fdcId}/nutrients`, "GET");
}

export function fetchCreditsBalance(): Promise<{
  scan_credits?: number;
  dietitian_credits?: number;
  consult_credits?: number;
  credits_available?: number;
}> {
  return request<{
    scan_credits?: number;
    dietitian_credits?: number;
    consult_credits?: number;
    credits_available?: number;
  }>(
    "/app/api/v1/credits/balance",
    "GET",
  );
}

export function fetchCreditServices(): Promise<{ services?: Array<{ key: string; cost: number }> }> {
  return request<{ services?: Array<{ key: string; cost: number }> }>("/app/api/v1/credits/services", "GET");
}

export function fetchAdminUserDailyCost(
  userId: string,
  startDate: string,
  endDate?: string | null,
): Promise<AdminUserDailyCostResponse> {
  const query = toQuery({
    start_date: startDate,
    end_date: endDate ?? undefined,
  });
  return request<AdminUserDailyCostResponse>(`/app/api/v1/admin/costs/users/${userId}/daily${query}`, "GET");
}

export function fetchAdminUserLifetimeCost(userId: string): Promise<AdminUserLifetimeCost> {
  return request<AdminUserLifetimeCost>(`/app/api/v1/admin/costs/users/${userId}/lifetime`, "GET");
}

export function fetchAdminUsersLifetimeCosts(
  limit = 50,
  cursor?: string | null,
): Promise<AdminUserLifetimeCostsPageResponse> {
  const safeLimit = Math.min(100, Math.max(1, Math.trunc(limit)));
  const query = toQuery({ limit: safeLimit, cursor: cursor ?? undefined });
  return request<AdminUserLifetimeCostsPageResponse>(`/app/api/v1/admin/costs/lifetimes${query}`, "GET");
}
